import {
  Box,
  Button,
  Grid,
  Link,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CampaignsContext } from "contexts/Campaigns";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { NewCampaignProvider } from "contexts/NewCampaign";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { PAGE_VISITED } from "constants/trackingProps";
import { trackEvent } from "utils/tracking";

import Campaign from "./Campaign";
import CampaignCard from "./CampaignCard";
import NewCampaign from "./NewCampaign";

const REF_OFFSET = 9;

export default function AllCampaigns() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCompleteOnboardingDialogOpen, incompleteProfile, noTemplates } =
    useContext(OnboardingDialogsContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const _isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const isMobileScreen =
    _isMobileScreen || (isMediumScreen && discoverTab !== null);

  const incompleteOnboarding = incompleteProfile || noTemplates;

  const {
    campaigns,
    getCampaigns,
    campaignsLoading,
    campaignsCount,
    campaignStatsLoading,
    campaignStats,
  } = useContext(CampaignsContext);

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  useEffect(() => {
    if (
      location.pathname.includes("new") &&
      !location.pathname.includes("type")
    ) {
      navigate("/dashboard/campaigns/new/type", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (campaignsCount === null && !campaignsLoading) {
      getCampaigns(true);
    }
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (
      isPaginationRefVisible &&
      campaignsCount &&
      campaigns.length < campaignsCount &&
      !campaignsLoading
    ) {
      getCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible, campaigns.length]);

  const createCampaignClicked = () => {
    if (incompleteOnboarding) {
      trackEvent("New Campaign Button Clicked - Complete Onboarding Modal");
      setCompleteOnboardingDialogOpen(true);
      return;
    }
    navigate("new/type");
    trackEvent("New Campaign Button Clicked");
  };
  return (
    <Box sx={{ my: 2, mx: { xs: 1, sm: 2 } }}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs="auto">
          <Typography variant="h5">My Campaigns</Typography>
        </Grid>
        <Grid item xs="auto">
          <Button
            variant="contained"
            size="small"
            onClick={createCampaignClicked}
            sx={{ cursor: "pointer" }}
            disableElevation
          >
            <Box component="span" className="fa-solid fa-plus" sx={{ mr: 1 }} />
            Create Campaign
          </Button>
        </Grid>
      </Grid>
      {campaignsLoading || campaigns.length > 0 ? (
        <>
          {!isMobileScreen && (
            <Grid container sx={{ mt: 4, px: 2 }}>
              <Grid item xs></Grid>
              <Grid item xs={2} sx={{ textAlign: "center" }}>
                <Typography color="textSecondary">Brands</Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center" }}>
                <Typography color="textSecondary">Sent</Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center" }}>
                <Typography color="textSecondary">Opens</Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center" }}>
                <Typography color="textSecondary">Responses</Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center" }}>
                <Typography color="textSecondary">Actions</Typography>
              </Grid>
            </Grid>
          )}
          <Grid
            container
            sx={{ mt: { xs: 2, lg: 1 }, pb: { xs: 10, md: 2 } }}
            rowGap={1}
          >
            {campaigns.map((campaign, index) => (
              <Grid
                key={campaign.id}
                ref={
                  (
                    REF_OFFSET > campaigns.length
                      ? campaigns.length - 1 === index
                      : campaigns.length - REF_OFFSET === index
                  )
                    ? paginationRef
                    : null
                }
                item
                xs={12}
              >
                <CampaignCard
                  campaign={campaign}
                  stats={campaignStats[campaign.id]}
                  statsLoading={campaignStatsLoading[campaign.id]}
                  isMobileScreen={isMobileScreen}
                />
              </Grid>
            ))}
          </Grid>
          {campaignsLoading && (
            <Grid container gap={2}>
              <Skeleton variant="rounded" width="100%" height={130} />
              <Skeleton variant="rounded" width="100%" height={130} />
              <Skeleton variant="rounded" width="100%" height={130} />
            </Grid>
          )}
        </>
      ) : (
        <Box display="flex" sx={{ mt: 4 }}>
          <Typography sx={{ maxWidth: "600px" }} color="textSecondary">
            You currently have no campaigns. Campaigns are used to send emails
            to multiple brands that fit in a certain niche using the same
            template.{" "}
            <strong>
              <Link onClick={createCampaignClicked} sx={{ cursor: "pointer" }}>
                Click here
              </Link>
            </strong>{" "}
            to create your first one!
          </Typography>
        </Box>
      )}
      <Routes>
        <Route
          path="new/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
        <Route
          path="campaign/:campaignId"
          element={
            <NewCampaignProvider>
              <Campaign />
            </NewCampaignProvider>
          }
        />
      </Routes>
    </Box>
  );
}
